import { gql } from '@apollo/client';
import type { DriverFaceCluster, PageableContent, PageableContentAPI, PageRequest, UpdateDriverFaceClusterRequest, UUID } from '@cobliteam/common-types';
import Alert from '@cobliteam/ui/components/Alert';
import graphQLRequester from '../../request/GraphQLRequester';
import type { GraphQLRestDataSourceError } from '../../request/Types';
import type { DriverFaceClusterAPI } from './definitions';
import { toDriverFaceClusterPage, toDriverFaceClusterUpdateRequestAPI } from './driverFaceClusterTransformer';

export class GraphQLCameraDriverFaceClusterRequester {
  private mutateAssignFaceClusterRequest = gql`
    mutation assignFaceCluster($input: [DriverFaceClusterUpdate]!) {
      assignFaceCluster(input: $input)
    }
  `;

  private queryGetDriverFaceClusterInfo = gql`
    query getDriverFaceClusterInfo(
      $page: Int
      $size: Int
      $sort: [String]
      $driverIds: [String]
      $hasBeenNominated: Boolean
      $withoutSampleImageUrls: Boolean
      $vehicleIds: [String]
    ) {
      driverFaceClustersInfo(
        page: $page
        size: $size
        sort: $sort
        driverIds: $driverIds
        hasBeenNominated: $hasBeenNominated
        withoutSampleImageUrls: $withoutSampleImageUrls
        vehicleIds: $vehicleIds
      ) {
        content {
          created_at
          id
          driver {
            id
            name
            fleet_id
            assigned
            phone_number
            first_license_acquirement_date
            license_expiration_date
            license_category
            driver_code
          }
          suggested_driver {
            id
            name
            fleet_id
            driver_code
          }
          profile_image_url {
            image_url
          }
          sample_image_urls {
            image_url
          }
          sample_image_url_count
          updated_at
        }
        empty
        first
        last
        number
        number_of_elements
        size
        total_elements
        total_pages
      }
    }
  `;

  public assignFaceCluster = (driverFaceClusterUpdate: UpdateDriverFaceClusterRequest[]): Promise<boolean> => {
    return graphQLRequester
      .mutate<{
        assignFaceCluster: boolean;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        mutation: this.mutateAssignFaceClusterRequest,
        variables: {
          input: toDriverFaceClusterUpdateRequestAPI(driverFaceClusterUpdate),
        },
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }

        return result.assignFaceCluster;
      });
  };

  public getDriverFaceClusterInfo = (
    { page, size, sort }: PageRequest,
    driverIds?: UUID[],
    hasBeenNominated?: Boolean,
    withoutSampleImageUrls?: boolean,
    vehicleIds?: UUID[]
  ): Promise<PageableContent<DriverFaceCluster>> =>
    graphQLRequester
      .query<{
        driverFaceClustersInfo: PageableContentAPI<DriverFaceClusterAPI>;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        query: this.queryGetDriverFaceClusterInfo,
        variables: {
          page,
          size,
          sort: sort ? `createdAt,${sort.order}` : null,
          driverIds,
          hasBeenNominated,
          withoutSampleImageUrls,
          vehicleIds,
        },
      })
      .then((result) => {
        if (result.errors !== undefined) {
          Alert.error('Ops! Não foi possível encontrar grupos de identificação facial. Tente novamente.');

          throw result.errors[0];
        }

        return toDriverFaceClusterPage(result.driverFaceClustersInfo);
      });
}

export default GraphQLCameraDriverFaceClusterRequester;
